// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { ClipboardCheck, AlertTriangle, Clock, CheckCircle, ChevronRight } from 'lucide-react';

const STATUS_CONFIG = {
  PENDING:           { label: 'Pending',           color: '#94A3B8', bg: '#F8FAFC' },
  IN_PROGRESS:       { label: 'In Progress',        color: '#3B82F6', bg: '#EFF6FF' },
  COMPLETED:         { label: 'Completed',          color: '#F59E0B', bg: '#FEF3C7' },
  AWAITING_APPROVAL: { label: 'Awaiting Approval',  color: '#8B5CF6', bg: '#EDE9FE' },
  APPROVED:          { label: 'Approved',            color: '#10B981', bg: '#D1FAE5' },
  REJECTED:          { label: 'Rejected',            color: '#EF4444', bg: '#FEE2E2' },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions]   = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats]         = useState({ today: 0, pending: 0, approved: 0, open_incidents: 0 });
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const today = new Date().toISOString().split('T')[0];
    const [{ data: sess }, { data: inc }] = await Promise.all([
      supabase.from('grocery_cleaning_sessions')
        .select('*, grocery_zones(zone_name, zone_color, zone_icon)')
        .gte('created_at', today)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase.from('grocery_incidents')
        .select('*')
        .in('status', ['OPEN', 'IN_PROGRESS'])
        .order('created_at', { ascending: false })
        .limit(5),
    ]);

    setSessions(sess || []);
    setIncidents(inc || []);

    const s = sess || [];
    setStats({
      today:         s.length,
      pending:       s.filter(x => x.session_status === 'AWAITING_APPROVAL').length,
      approved:      s.filter(x => x.session_status === 'APPROVED').length,
      open_incidents:(inc || []).length,
    });
    setLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Dashboard</h1>
          <p className="page-subtitle">
            Welcome back, {user?.full_name || user?.email?.split('@')[0]} — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/zones')}>
          <ClipboardCheck size={16} />
          Start Cleaning Session
        </button>
      </div>

      <div className="page-body">
        {/* Stats */}
        <div className="stats-grid">
          {[
            { label: "Today's Sessions",   value: stats.today,          icon: '📋', color: '#3B82F6' },
            { label: 'Awaiting Approval',  value: stats.pending,        icon: '⏳', color: '#8B5CF6' },
            { label: 'Approved Today',     value: stats.approved,       icon: '✅', color: '#10B981' },
            { label: 'Open Incidents',     value: stats.open_incidents, icon: '⚠️', color: '#EF4444' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Recent Sessions */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#134E4A', margin: 0 }}>
                Today's Sessions
              </h3>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/zones')}>
                New Session
              </button>
            </div>

            {sessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94A3B8' }}>
                No sessions started today.
              </div>
            ) : (
              sessions.slice(0, 6).map(s => {
                const cfg = STATUS_CONFIG[s.session_status] || STATUS_CONFIG.PENDING;
                return (
                  <div
                    key={s.id}
                    onClick={() => navigate(`/zones/${s.zone_id}/session`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 0', borderBottom: '1px solid #F0FDF9',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: s.grocery_zones?.zone_color + '20',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, flexShrink: 0,
                    }}>
                      {s.grocery_zones?.zone_icon || '🧹'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#134E4A' }}>
                        {s.grocery_zones?.zone_name || 'Unknown Zone'}
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        {new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <span className="badge" style={{ background: cfg.bg, color: cfg.color }}>
                      {cfg.label}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Open Incidents */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#134E4A', margin: 0 }}>
                Open Incidents
              </h3>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/incidents')}>
                Report Incident
              </button>
            </div>

            {incidents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94A3B8' }}>
                <CheckCircle size={32} style={{ margin: '0 auto 8px', display: 'block', color: '#10B981' }} />
                No open incidents!
              </div>
            ) : (
              incidents.map(inc => (
                <div
                  key={inc.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 0', borderBottom: '1px solid #F0FDF9',
                  }}
                >
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    background: inc.severity === 'critical' ? '#DC2626'
                      : inc.severity === 'high' ? '#EF4444'
                      : inc.severity === 'medium' ? '#F59E0B' : '#94A3B8',
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#134E4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inc.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>
                      {inc.incident_type} · {inc.severity}
                    </div>
                  </div>
                  <span className="badge badge-danger" style={{ textTransform: 'uppercase', fontSize: 9 }}>
                    {inc.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
